import React from 'react';
import { useDispatch } from 'react-redux';
import { Formik, FieldArray } from 'formik';
import NumberFormat from 'react-number-format';
import * as yup from 'yup';
import {
  Button,
  Grid,
  TextField,
  CardHeader,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  FormHelperText,
  Checkbox,
  Card,
  FormControl,
  MenuItem,
  Icon,
  Fab
} from '@material-ui/core';
import {addCustomOptionApi} from 'api';
import { uploadFileTypeUnsupported } from 'redux/actions/general';
import {fileToBase64} from 'utils';
import {cacheSwatchImage} from './swatchImageCache';


const emptyCustomOptionValue = {
  price: '',
  price_type: 'fixed',
  sort_order: 1,
  title: '',
  images: [],
  manage_stock: false,
  qty: 0
};
const AddCustomOption = ({open, onClose, options = [], product_sku, types}) => {
  const dispatch = useDispatch();
  const customOptionsSortOrders = options.map((option) => option.sort_order);
  const setNewCustomOptionValue = (values) => {
    const sortOrders = values.map((value) => Number(value.sort_order));
    const maxOrder = sortOrders.reduce((a, b) => Math.max(a, b));
    return {
      price: '',
      price_type: 'fixed',
      sort_order: maxOrder + 1,
      title: '',
      images: []
    };
  };
  return (
    <Dialog
      fullWidth
      maxWidth={'md'}
      open={open}
      onClose={() => onClose(false)}
      aria-labelledby="add-custom-option"
    >
      <DialogContent>
        <Formik
          initialValues={{
            product_sku,
            title: '',
            sort_order: (options.length + 1),
            type: '',
            is_require: '',
            is_swatch: false,
            dermpro_option_gallery: 0,
            dermpro_option_image_mode: 0,
            values: [emptyCustomOptionValue]
          }}
          enableReinitialize
          validationSchema={yup.object({
            title: yup
              .string()
              .required('Option name is required')
              .min(2, 'Must be at least 2 characters'),
            sort_order: yup
              .number()
              .notOneOf(customOptionsSortOrders, 'Sort order already specified')
              .nullable().typeError('Allowed numbers only')
              .integer('Order must be an integer')
              .min(0, 'Invalid sort_order number')
              .required('Order is required'),
            type: yup
              .string()
              .required('Type is required'),
            is_require: yup
              .boolean(),
            is_swatch: yup
              .boolean(),
            values: yup
              .array()
              .min(1, 'Custom option should have at least one value')
              .required('Field is required')
              .of(
                yup.object({
                  title: yup
                    .string()
                    .required('Value name is required')
                    .min(2, 'Must be at least 2 characters'),
                  sort_order: yup
                    .number()
                    .nullable().typeError('Allowed numbers only')
                    .test('sort_order', 'Sort order already specified', (value, context) => {
                      const sortOrders = context.options.from[1].value.values.map((value) => Number(value.sort_order));
                      const index = context.path.replace(/[^0-9]/g, '');
                      const filteredOrders = sortOrders.filter((_, i) => i != index);
                      return !filteredOrders.includes(Number(value));
                    })
                    .integer('Order must be an integer')
                    .min(1, 'Invalid sort_order number')
                    .required('Order is required'),
                  price: yup
                    .number()
                    .nullable().typeError('Allowed numbers only')
                    .min(0, 'Custom option price must be greater than or equal to 0'),
                  manage_stock: yup
                    .boolean(),
                  qty: yup
                    .number()
                    .when(
                      'manage_stock',
                      {
                        is: true,
                        then: yup
                          .number()
                          .typeError('Allowed numbers only')
                          .min(0, 'Quantity must be greater than or equal to 0')
                          .required('Field is required')
                      }
                    )
                })
              )
              .when('is_swatch', {
                is: true,
                then: yup.array().of(
                  yup.object({
                    images: yup
                      .array()
                      .min(1, 'Image is required')
                  })
                ),
              })
          })}

          onSubmit={(values) => {
            values.values.forEach((value) => {
              if (value.images?.[0]?.base64) {
                cacheSwatchImage(values.product_sku, value.title, value.sort_order, value.images[0].base64);
              }
            });

            addCustomOptionApi({ dispatch, option: values});
            onClose(false);
          }}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            handleChange,
            handleSubmit,
            setFieldValue
          }) => (
            <>
              <form onSubmit={handleSubmit}>
                <CardHeader
                  className="px-0"
                  subheader="Custom Option"
                />
                <CardContent className="px-0 w-full flex justify-between">
                  <TextField
                    className="mx-3 w-full"
                    name="title"
                    label="Option Title"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputProps={{ className: 'text-center' }}
                    error={touched.title && Boolean(errors.title)}
                    helperText={touched.title && errors.title}
                  />
                  <TextField
                    className="mx-3 w-full"
                    name="sort_order"
                    label="Order"
                    value={values.sort_order}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputProps={{ className: 'text-center' }}
                    error={touched.sort_order && Boolean(errors.sort_order)}
                    helperText={touched.sort_order && errors.sort_order}
                  />
                  <TextField
                    className="mx-3 w-full"
                    name="type"
                    select
                    label="Type"
                    value={values.type}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    inputProps={{ className: 'text-center' }}
                    error={touched.type && Boolean(errors.type)}
                    helperText={touched.type && errors.type}
                    disabled={values.is_swatch}
                  >
                    {types.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <FormControl aria-label="position" className="w-full max-w-150" row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.is_require}
                          color="primary"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          name="is_require"
                          disabled={values.is_swatch}
                        />}
                      label="Required"
                    />
                  </FormControl>
                  <FormControl aria-label="is_swatch" className="w-full max-w-150" row>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={values.is_swatch}
                          color="primary"
                          onChange={(e) => {
                            const {checked} = e.target;
                            handleChange(e);

                            if (checked) {
                              setFieldValue('type', 'drop_down');
                              setFieldValue('is_require', 1);
                              setFieldValue('dermpro_option_gallery', 1);
                              setFieldValue('dermpro_option_image_mode', 1);
                            } else {
                              setFieldValue('type', '');
                              setFieldValue('dermpro_option_gallery', 0);
                              setFieldValue('dermpro_option_image_mode', 0);
                            }
                          }}
                          onBlur={handleBlur}
                          name="is_swatch"
                        />}
                      label="Is Swatch"
                    />
                  </FormControl>
                </CardContent>
                <FieldArray name="values">
                  {({remove, push}) => (
                    <>
                      {values.values.map((_, idx) => (
                        <Grid item xs={12} key={`custom-value-${idx}`} style={{ display: values ? 'block' : 'none' }}>
                          <Card variant="outlined" className="px-4 pb-4 my-4">
                            <CardHeader
                              className="px-0"
                              action={
                                values.values.length > 1 ?
                                  <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => remove(idx)}
                                  >
                                    Delete
                                    <Icon>delete</Icon>
                                  </Button>
                                  : null
                              }
                              subheader="Custom Option Value"
                            />
                            <CardContent className="px-1 flex justify-between">
                              <TextField
                                className="mx-3 w-full"
                                name={`values[${idx}].title`}
                                label="Value Title"
                                value={values.values[idx].title}
                                onChange={(e) => {
                                  handleChange(e);

                                  if (values.values[`${idx}`].images.length) {
                                    setFieldValue(`values[${idx}].images[0].title_text`, e.target.value);
                                  }
                                }}
                                onBlur={(e) => {
                                  handleBlur(e);

                                  if (values.values[`${idx}`].images.length) {
                                    setFieldValue(`values[${idx}].images[0].title_text`, e.target.value);
                                  }
                                }}
                                inputProps={{ className: 'text-center' }}
                                error={touched.values?.length &&
                                    errors.values?.length &&
                                    touched?.values[idx]?.title &&
                                    Boolean(errors?.values[idx]?.title)
                                }
                                helperText={touched.values?.length &&
                                    errors.values?.length &&
                                    touched?.values[idx]?.title &&
                                    errors?.values[idx]?.title
                                }
                              />
                              <NumberFormat
                                name={`values[${idx}]sort_order`}
                                label="Order"
                                decimalScale={0}
                                className="mx-3 w-full"
                                value={values.values[idx].sort_order}
                                onValueChange={(val) => setFieldValue(`values[${idx}]sort_order`, val.floatValue)}
                                customInput={TextField}
                                inputProps={{ className: 'text-center' }}
                                error={touched.values?.length &&
                                    errors.values?.length &&
                                    touched?.values[idx]?.sort_order &&
                                    Boolean(errors?.values[idx]?.sort_order)
                                }
                                helperText={touched.values?.length &&
                                    errors.values?.length &&
                                    touched?.values[idx]?.sort_order &&
                                    errors?.values[idx]?.sort_order
                                }
                              />
                              <TextField
                                className="mx-3 w-full"
                                label="Price"
                                name={`values.${idx}.price`}
                                value={values.values[idx].price}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                inputProps={{ className: 'text-center' }}
                                error={touched.values?.length &&
                                    errors.values?.length &&
                                    touched?.values[idx]?.price &&
                                    Boolean(errors?.values[idx]?.price)
                                }
                                helperText={touched.values?.length &&
                                    errors.values?.length &&
                                    touched?.values[idx]?.price &&
                                    errors?.values[idx]?.price
                                }
                              />
                              <FormControlLabel
                                label="Manage Stock"
                                className="mx-3 w-full"
                                control={
                                  <Checkbox
                                    color="primary"
                                    checked={values.values[idx]?.manage_stock}
                                    onChange={({target: {checked}}) => setFieldValue(`values[${idx}].manage_stock`, checked)}

                                  />
                                }
                              />
                              {values.values[idx].manage_stock &&
                                <TextField
                                  className="mx-3 w-full"
                                  name={`values[${idx}].qty`}
                                  label="Quantity"
                                  value={values.values[idx].qty}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  inputProps={{
                                    className: 'text-center',
                                    shrink: 'true'
                                  }}
                                  error={touched.values?.length &&
                                    errors.values?.length &&
                                    touched?.values[idx]?.qty &&
                                    Boolean(errors?.values[idx]?.qty)}
                                  helperText={touched.values?.length &&
                                    errors.values?.length &&
                                    touched?.values[idx]?.qty &&
                                    errors?.values[idx]?.qty
                                  }
                                />
                              }
                            </CardContent>
                            {values.is_swatch &&
                            <>
                              <CardHeader
                                className="px-0 mt-5"
                                subheader="Image for swatch custom option:"
                              />
                              <CardContent
                                className={`border-1-light-grey border-radius-4 p-8 border-dashed flex flex-wrap
                                ${(errors.values?.length && Boolean(errors?.values[idx]?.images) && 'border-error')}
                                `}
                              >
                                {!values.values[idx].images.length ?
                                  <Card elevation={10} className="flex-column h-full items-center justify-center p-8">
                                    <div>
                                      <Icon fontSize="large" color="primary">cloud_upload</Icon>
                                    </div>
                                    <span className="font-bold text-14 text-brand">Upload New Image</span>
                                    <span className="text-12 mb-4 text-muted">Files supported: JPG and PNG</span>
                                    <label htmlFor="upload-swatch-image">
                                      <Fab
                                        component="span"
                                        variant="extended"
                                        className="border-radius-30 capitalize bg-white text-14 h-30px text-primary font-bold border-1-primary box-shadow-none"
                                      >
                                        Browse file
                                      </Fab>
                                    </label>
                                    <input
                                      className="hidden"
                                      onChange={async (event) => {
                                        const {files, value} = event.target;

                                        const fileTypeDotIndexPosition = value.lastIndexOf('.') + 1;
                                        const slicedFileTypeFromFilePath = value.slice(fileTypeDotIndexPosition);
                                        // image validation regex
                                        const acceptableFormats = 'png|jpeg';
                                        const hasImageFile = new RegExp(`(image\\/(${acceptableFormats}))`, 'gi');

                                        if (!hasImageFile.test(event.target.files[0]?.type) && !hasImageFile.test(slicedFileTypeFromFilePath)) {
                                          dispatch(uploadFileTypeUnsupported());
                                        } else {
                                          const file = await fileToBase64(files[0]);
                                          const base64Image = btoa(file);

                                          const optionValueImages = [
                                            {
                                              base64: base64Image,
                                              sort_order: 1,
                                              title_text: values.values[`${idx}`].title
                                            }
                                          ];
                                          setFieldValue(`values[${idx}].images`, optionValueImages);
                                        }
                                      }}
                                      id="upload-swatch-image"
                                      type="file"
                                      accept="image/png, image/jpeg"
                                    />
                                  </Card>
                                  :
                                  <>
                                    {values.values[idx].images.map((imageItem, index) => (
                                      <div
                                        key={`swatch-image-${index}`}
                                        className="p-2 max-w-180 border-radius-4 overflow-visible elevation-z5 relative"
                                      >
                                        <Icon
                                          fontSize="small"
                                          className="absolute text-muted cursor-pointer"
                                          style={{right: '-5px', top: '-5px'}}
                                          title="Delete Image"
                                          onClick={() => {
                                            if (imageItem.option_type_image_id) {
                                              const filteredImages = values.images.filter((image) => (
                                                image.option_type_image_id !== imageItem.option_type_image_id
                                              ));
                                              setFieldValue(`values[${idx}].images`, filteredImages);
                                            } else {
                                              setFieldValue(`values[${idx}].images`, []);
                                            }
                                          }}
                                        >
                                          cancel
                                        </Icon>
                                        <img
                                          src={imageItem.option_type_image_id
                                            ? imageItem.image_url
                                            : `data:image/png;base64,${imageItem.base64}`
                                          }
                                          alt={imageItem.title_text}
                                          className="max-w-full"
                                        />
                                      </div>
                                    ))}
                                  </>
                                }
                              </CardContent>
                              {values.is_swatch &&
                                touched.values?.length &&
                                errors.values?.length &&
                                touched?.values[idx]?.images &&
                                Boolean(errors?.values[idx]?.images) &&
                                  <FormHelperText error>
                                    {errors.values[idx].images}
                                  </FormHelperText>
                              }
                            </>
                            }
                          </Card>
                        </Grid>
                      ))}
                      <Button
                        onClick={() => push(setNewCustomOptionValue(values.values))}
                        color="primary"
                        variant="contained"
                      >
                        Add value
                      </Button>
                    </>
                  )}
                </FieldArray>
                <DialogActions className="m-4">
                  <Button
                    className="text-muted"
                    onClick={() => onClose(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    color="primary"
                    variant="contained"
                    type="submit"
                  >
                    Create Option
                  </Button>
                </DialogActions>
              </form>
            </>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};
export default AddCustomOption;
